import { ArrowLeftOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import { Button, Form, Input, Row, Select, Typography } from 'antd';
import { CreateDishComponentSection } from './component-section.form';
import CustomLabel from '@/components/custom-label';
import { useCallback, useEffect, useState } from 'react';
import { useProductCache } from '@/hooks/use-product-cache.hook';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  editDish,
  getListDish,
  getDetailDish,
  uploadImage,
  watchDetailDishById
} from '@/redux/slices/dish.slice';
import { useQuery } from '@/hooks/query.hook';
import { PageURLs } from '@/utils/navigate';
import {
  watchListDishAge,
  watchListDishCookingMethod,
  watchListSourceDish,
  watchListStructureMealDish,
  watchListSizeDish,
  watchListRegionDish,
  watchListTagDish,
  watchListDish,
  watchDishMessage,
  watchDishLoading,
  resetMessages
} from '@/redux/slices/dish.slice';
import { setMessages } from '@/redux/slices/app.slice';
import IMAGE_DEFAULT from '@/assets/images/image_default.png';
import ProductInfoSection from './ProductInfoSection';
import CKEditorMaster from '@/components/CKEditor/CKEditorMaster';
import { REQUEST_VALIDATION } from '@/constants/message.constant';

type Size = {
  sizeType: string;
  sizeId?: string;
  image?: string;
  dataBig: {
    keyFormula: number;
    id?: string;
    formulaType?: string;
    data: {
      keyComponent: number,
      id?: string,
      ingredient: { name: string, label: string },
      weight: { name: string },
      processingMethod: { name: string },
    }[]
  }[]
}

type UploadResponseItem = {
  linker: { size: string };
  url: string;
};

const DishManagementEditForm = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [sizes, setSizes] = useState<Size[]>([]);
  const [removedSizeIds, setRemovedSizeIds] = useState<string[]>([]);
  const detailDish = useAppSelector(watchDetailDishById);
  const { push, dishId, ageGroupId, dishSizeId } = useQuery();
  const listRegion = useAppSelector(watchListRegionDish);
  const listDishAge = useAppSelector(watchListDishAge);
  const listDishCookingMethod = useAppSelector(watchListDishCookingMethod);
  const listSourceDish = useAppSelector(watchListSourceDish);
  const listStructureMealDish = useAppSelector(watchListStructureMealDish);
  const listSizeDish = useAppSelector(watchListSizeDish);
  const listTagDish = useAppSelector(watchListTagDish);
  const listDish = useAppSelector(watchListDish);
  const messageInfo = useAppSelector(watchDishMessage);
  const loading = useAppSelector(watchDishLoading);
  const [isProductInfoExpanded, setIsProductInfoExpanded] = useState(false);
  const [activeProductIndex, setActiveProductIndex] = useState<number>(0);

  const avnProducts = useProductCache(sizes);

  useEffect(() => {
    dispatch(getDetailDish({ id: dishId, ageGroupId: ageGroupId, dishSizeId: dishSizeId }));
  }, [dispatch, dishId, ageGroupId, dishSizeId]);

  const handleGoBack = useCallback(() => {
    push(PageURLs.ofDishManagement());
  }, [push]);

  const ageDishGroupOptions = listDishAge.map((item) => ({
    value: item.id,
    label: item.name
  }));

  const dishCookingMethodGroupOptions = listDishCookingMethod.map((item) => ({
    value: item.id,
    label: item.name
  }));

  const sourceDishGroupOptions = listSourceDish.map((item) => ({
    value: item.id,
    label: item.name
  }));

  const structureMealDishGroupOptions = listStructureMealDish.map((item) => ({
    value: item.id,
    label: item.name
  }));

  const regionDishGroupOptions = listRegion.map((item) => ({
    value: item.regionId,
    label: item.regionName
  }));

  const tagDishGroupOptions = listTagDish.map((item) => ({
    value: item.tagName,
    label: item.tagName
  }));

  const labelColSpan = 10;

  const onFinish = async (values: any) => {
    let regionValue = values.region;

    if (Array.isArray(values.region)) {
      regionValue = values.region[0]?.value;
    } else if (values.region && typeof values.region === 'object') {
      regionValue = values.region.value;
    }

    const transformSizes = await transformSizesForPayload(sizes);
    const body: any = {
      DISH_ID: values.id,
      NAME: values.name,
      AGE_GROUP_ID: values.age,
      REGION_ID: regionValue,
      EXTERNAL_DISH_TYPE_ID: values.externalSource,
      COOKING_METHOD_ID: values.cookingMethod,
      COOKING_INSTRUCTION: values.processingMethod ?? '',
      TIPS: values.tip ?? '',
      MEAL_STRUCTURE_ID: values.structuralGroup,
      SMALL_IMAGE: "",
      LARGE_IMAGE: "",
      sizes: transformSizes,
      tags: values.label,
      avnProducts: avnProducts,
      listSizeToDelete: removedSizeIds
    }
    const id = detailDish?.id || '';
    await dispatch(editDish({ id, payload: body, ageGroupId }));
    await dispatch(getListDish({}));
    push(PageURLs.ofDishManagement());
  };

  const transformSizesForPayload = async (sizes: Size[]) => {
    const images: ImageUploadItem[] = sizes
      .filter((size): size is Size & { image: string } => !!size.image)
      .map((size) => ({
        linker: { size: size.sizeType },
        image: size.image,
      }));

    const uploadedResult = images.length
      ? await dispatch(uploadImage({ images })).unwrap()
      : [];

    const imageMap = new Map(
      (uploadedResult as UploadResponseItem[]).map((item) => {
        const cleanUrl = item.url?.split('?')[0] ?? item.url;
        return [item.linker.size, cleanUrl];
      })
    );

    return sizes.map((size) => ({
      SIZE_TYPE: size.sizeType,
      IMAGE: imageMap.get(size.sizeType) || '',
      ID: size.sizeId ?? undefined,
      salt_formulas: size.dataBig.map((formula) => ({
        ID: formula.id || '',
        FORMULA_TYPE: `CTM${formula.keyFormula + 1}`,
        ingredients: formula.data.map((component) => ({
          ID: component.id || '',
          INGREDIENT_ID: typeof component.ingredient === 'object' && component.ingredient !== null
            ? component.ingredient.name
            : component.ingredient || "",
          WEIGHT: typeof component.weight === 'object' && component.weight !== null
            ? component.weight.name
            : component.weight || "",
          PROCESSING_METHOD: typeof component.processingMethod === 'object' && component.processingMethod !== null
            ? component.processingMethod.name
            : component.processingMethod || "",
        }))
      }))
    }));
  };

  useEffect(() => {
    if (detailDish) {
      const tags = detailDish.tags?.map((tagId) => {
        const foundTag = listTagDish.find((tag) => tag.tagName === tagId);
        return foundTag ? foundTag.tagName : null;
      }).filter(Boolean);

      const region = detailDish?.regionId
        ? [
          {
            value: detailDish.regionId,
            label: listRegion.find(region => region.regionId === detailDish.regionId)?.regionName || '',
          }
        ]
        : [];

      form.setFieldsValue({
        id: detailDish.id,
        name: detailDish.name,
        age: detailDish.ageGroupId,
        region: region,
        label: tags,
        externalSource: detailDish.externalDishTypeId,
        cookingMethod: detailDish.cookingMethodId,
        processingMethod: detailDish.cookingInstruction,
        tip: detailDish.tips,
        structuralGroup: detailDish.mealStructureId,
      });

      const newSizes: Size[] = (detailDish?.sizes ?? []).map((size) => ({
        sizeType: size.sizeType ?? '',
        sizeId: size.id,
        image: size.image ?? '',
        dataBig: (size.saltFormulas ?? []).map((formula, formulaIndex) => {
          const formulaTypeMatch = formula.formulaType?.match(/CTM(\d+)/);
          const keyFormula = formulaTypeMatch ? parseInt(formulaTypeMatch[1]) - 1 : formulaIndex;
          
          return {
            keyFormula: keyFormula,
            id: formula.id,
            formulaType: formula.formulaType,
            data: (formula.ingredients ?? []).map((item, ingIndex) => ({
              keyComponent: ingIndex,
              id: item.id,
              ingredient: { name: item.ingredientId, label: item.ingredientName },
              weight: { name: item.weight },
              processingMethod: { name: item.processingMethod },
            })),
          };
        }),
      }));

      setSizes(newSizes);
    }
  }, [detailDish, form, listTagDish, listRegion]);

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );

      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo]);

  useEffect(() => {
    setSizes([]);
  }, [setSizes])

  return (
    <Form form={form} css={rootStyles} onFinish={onFinish}>
      <Row css={boxStyles} align='middle' gutter={16}>
        <Button
          type='text'
          icon={<ArrowLeftOutlined />}
          onClick={handleGoBack}
        >
          {' '}
          <Typography.Title level={5} style={{ marginBottom: 0 }}>
            Tự nhập từng món ăn
          </Typography.Title>
        </Button>
      </Row>

      <Row css={boxStyles} align='middle' gutter={16}>
        <Row css={fieldStyles}>
          <Form.Item
            name={'age'}
            label={CustomLabel({ text: 'Độ tuổi', isRequired: true })}
            labelCol={{ span: labelColSpan }}
            rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
          >
            <Select
              placeholder='Chọn độ tuổi'
              options={ageDishGroupOptions}
              onChange={() => {
                form?.validateFields(['id']);
              }}
              disabled
            />
          </Form.Item>
        </Row>
        <Row css={fieldStyles}>
          <Form.Item
            name={'id'}
            label={CustomLabel({ text: 'ID món ăn', isRequired: true })}
            labelCol={{ span: labelColSpan }}
            rules={[
              { required: true, message: 'Vui lòng điền thông tin!' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();

                  const selectedAge = form?.getFieldValue('age');

                  const isDuplicated = listDish.some(
                    (dish) =>
                      dish.dishId === value &&
                      dish.ageGroupId === selectedAge &&
                      !(dish.dishId === dishId && dish.ageGroupId === ageGroupId)
                  );
                  return isDuplicated
                    ? Promise.reject(REQUEST_VALIDATION.ERROR_ID)
                    : Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder='Nhập ID món ăn' disabled/>
          </Form.Item>
        </Row>
        <Row css={fieldStyles}>
          <Form.Item
            name={'name'}
            label={CustomLabel({ text: 'Tên món ăn', isRequired: true })}
            labelCol={{ span: labelColSpan }}
            rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
          >
            <Input placeholder='Nhập tên món ăn' />
          </Form.Item>
        </Row>
        <Row css={fieldStyles}>
          <Form.Item
            name={'region'}
            label={CustomLabel({ text: 'Món ăn theo miền', isRequired: true })}
            labelCol={{ span: labelColSpan }}
            rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
          >
            <Select
              placeholder='Chọn miền món ăn'
              options={regionDishGroupOptions}
            />
          </Form.Item>
        </Row>
        <Row css={fieldStyles}>
          <Form.Item
            name={'label'}
            label={'Gắn nhãn món ăn'}
            labelCol={{ span: labelColSpan }}
          >
            <Select
              mode='multiple'
              placeholder='Chọn nhãn món ăn'
              options={tagDishGroupOptions}
            />
          </Form.Item>
        </Row>
        <Row css={fieldStyles}>
          <Form.Item
            name={'externalSource'}
            label={CustomLabel({ text: 'Nguồn món ăn bên ngoài', isRequired: true })}
            labelCol={{ span: labelColSpan }}
            rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
          >
            <Select
              placeholder='Chọn nguồn món ăn'
              options={sourceDishGroupOptions}
            />
          </Form.Item>
        </Row>
        <Row css={fieldStyles}>
          <Form.Item
            name={'cookingMethod'}
            label={CustomLabel({
              text: 'Nhóm món ăn theo phương pháp nấu',
              isRequired: true
            })}
            labelCol={{ span: labelColSpan }}
            rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
          >
            <Select
              placeholder='Chọn nhóm món ăn theo phương pháp nấu'
              options={dishCookingMethodGroupOptions}
            />
          </Form.Item>
        </Row>
        <Row css={fieldStyles}>
          <Form.Item
            name={'structuralGroup'}
            label={CustomLabel({
              text: 'Nhóm món ăn theo cấu trúc bữa ăn',
              isRequired: true
            })}
            labelCol={{ span: labelColSpan }}
            rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
          >
            <Select
              placeholder='Chọn nhóm món ăn theo cấu trúc bữa ăn'
              options={structureMealDishGroupOptions}
            />
          </Form.Item>
        </Row>
      </Row>

      <Row css={boxStyles} align='middle' gutter={16}>
        <CreateDishComponentSection 
          sizeRecordData={sizes} 
          setSizes={setSizes} 
          listSizeDish={listSizeDish} 
          form={form}
          setRemovedSizeIds={setRemovedSizeIds}
          existingSizes={detailDish?.listSize}
        />
      </Row>

      <Row css={boxStyles} align='middle' gutter={16}>
        <Typography.Title level={5}>Công thức nấu</Typography.Title>

        <Form.Item
          label='Cách chế biến'
          name={`processingMethod`}
          labelCol={{ span: 24 }}
          css={wideQuillFormItem}
        >
          <div css={localCKEditorStyles}>
            <CKEditorMaster
              key={form.getFieldValue('processingMethod')}
              content={form.getFieldValue('processingMethod')}
              handleUpdateContent={(val: string) => form.setFieldsValue({ processingMethod: val })}
              isDisabled={false}
              MAX_CHAR_LENGTH={5000}
              placeholder="Nhập công thức nấu của món ăn"
            />
          </div>
        </Form.Item>
        <Form.Item
          label='Mẹo hay mách nhỏ'
          name={`tip`}
          labelCol={{ span: 24 }}
          css={wideQuillFormItem}
        >
          <div css={localCKEditorStyles}>
            <CKEditorMaster
              key={form.getFieldValue('tip')}
              content={form.getFieldValue('tip')}
              handleUpdateContent={(val: string) => form.setFieldsValue({ tip: val })}
              isDisabled={false}
              MAX_CHAR_LENGTH={5000}
              placeholder="Nhập công thức nấu của món ăn"
            />
          </div>
        </Form.Item>
        <Form.Item
          label='Thông tin thêm'
          name={`extra`}
          labelCol={{ span: 24 }}
        >
          <div css={extraInfoStyles}>
            {avnProducts.length > 0 ? (
              <>
                <p>Sản phẩm AVN</p>
                <div>
                  {avnProducts.map((item, idx) => (
                    <div 
                      key={`${item?.idFood}-${idx}`} 
                      css={extraInfoCardStyles}
                      onClick={() => { setActiveProductIndex(idx); setIsProductInfoExpanded(true); }}
                      style={{ cursor: 'pointer' }}
                    >
                      <img src={item?.logo || item?.thumbnail || IMAGE_DEFAULT} alt={item?.name} />
                      <div>
                        <p>{item?.name}</p>
                        <p className="short-desc">{item.shortDesc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p style={{ gridColumn: '1 / -1' }}>Hệ thống tự động hiển thị sản phẩm AVN có trong danh sách thực phẩm của công thức món ăn</p>
            )}
          </div>
        </Form.Item>
      </Row>

      {isProductInfoExpanded && (
        <ProductInfoSection
          isExpanded={isProductInfoExpanded}
          onToggle={() => setIsProductInfoExpanded(false)}
          products={avnProducts}
          initialIndex={activeProductIndex}
        />
      )}

      <Row css={groupButtonStyles} align='top' gutter={16}>
        <Button
          type='default'
          size='large'
          onClick={handleGoBack}
        >
          Hủy
        </Button>

        <Form.Item noStyle>
          <Button type='primary' size='large' htmlType='submit' loading={loading}>
            Lưu
          </Button>
        </Form.Item>
      </Row>
    </Form>
  );
};
export default DishManagementEditForm;
const rootStyles = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 1rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  display: flex;
  flex-direction: column;
  align-items: start;
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const fieldStyles = css`
  @media (max-width: 1024px) {
    width: 100%;
  }

  width: 60%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .ant-form-item {
    width: 100%;
    margin-bottom: 6px;

    .ant-form-item-row {
      display: flex;
      justify-content: space-between;
    }

    .ant-form-item-label {
      text-align: start !important;

      label {
        flex: 1;
        white-space: normal;
        height: auto;
        line-height: 1.5;

        &::after,
        &::before {
          display: none !important;
        }
      }
    }
  }

  input {
    flex: 1;
  }
`;

const groupButtonStyles = css`
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }

  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  gap: 1.6rem;

  button {
    padding: 1rem 10rem;
  }
`;

const localCKEditorStyles = css`
  margin-top: 8px;
  ul li,
  ol li,
  nav ul {
    list-style: revert !important;
  }

  .ck.ck-content.ck-editor__editable.ck-rounded-corners.ck-editor__editable_inline {
    padding: 0.5rem 2.8rem;
  }
`;

const extraInfoStyles = css`
  display: grid;
  grid-template-columns: 1fr 6fr;
  gap: 1rem;
  width: 100%;

  > p {
    font-family: Roboto;
    font-weight: 400;
    font-style: italic;
    font-size: 14px;
    color: rgba(199, 199, 199, 1);
  }

  > div {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 1rem;
  }
`

const extraInfoCardStyles = css`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.8rem;
  margin-bottom: 1rem;
  border: 1px solid var(--gray-light-color);

  img {
    width: 4.8rem;
    height: 4.8rem;
    object-fit: cover;
  }

  p {
    font-family: Roboto;
    font-weight: 400;
    font-style: italic;
    font-size: 14px;
    color: rgba(24, 144, 255, 1);
  }

  .short-desc {
    color: var(--text-color, #333);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 22rem;
  }
`

const wideQuillFormItem = css`
  width: 100%;
`;
